import { useState, useEffect, useRef } from 'react';
import { getStoreItems, getProductByBarcode, getCategories } from '../lib/api';
import { Store, BookmarkCheck, Trash2, Plus, ScanBarcode, Warehouse, ArrowLeft, Camera, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { useRefresh } from '../components/Layout';
import { BarcodeScanner } from '../components/BarcodeScanner';

// Generate random barcode
const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INT${timestamp}${random}`;
};

export const StorePage = () => {
    const { refreshStats } = useRefresh();
    const [storeItems, setStoreItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);
    const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
    const [isMoveToDepozitModalOpen, setIsMoveToDepozitModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [moveToDepozitQty, setMoveToDepozitQty] = useState('1');
    const [moveToDepozitLocation, setMoveToDepozitLocation] = useState('P01');
    const [reserveData, setReserveData] = useState({
        customer_id: '',
        quantity: '1',
        notes: ''
    });
    const [newCustomerId, setNewCustomerId] = useState('');
    
    // Barcode scanning state
    const [barcodeInput, setBarcodeInput] = useState('');
    const [currentProduct, setCurrentProduct] = useState(null);
    const [step, setStep] = useState(1);
    const [showNewProductForm, setShowNewProductForm] = useState(false);
    const [newProductBarcode, setNewProductBarcode] = useState('');
    const [newProductName, setNewProductName] = useState('');
    const [newProductPrice, setNewProductPrice] = useState('');
    const [newProductCategory, setNewProductCategory] = useState('');
    const [stockQuantity, setStockQuantity] = useState('1');
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const barcodeInputRef = useRef(null);
    
    // Categories autocomplete
    const [categories, setCategories] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    // Locations
    const locations = ['P01', 'P02', 'P03', 'P04', 'P05', 'P06', 'P07', 'P08', 'P09', 'P10'];

    useEffect(() => {
        loadData();
        loadCategories();
    }, []);
    
    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    const exportV1StoreExcel = async () => {
        setIsExporting(true);
        try {
            const response = await api.get('/v1/store/export/excel', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Magazin_V1_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '-')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel exportat cu succes');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la export');
        }
        setIsExporting(false);
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const storeRes = await getStoreItems();
            setStoreItems(storeRes.data);
        } catch (err) {
            toast.error('Eroare la încărcarea datelor');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const openReserveModal = (item) => {
        setSelectedItem(item);
        setReserveData({
            customer_id: '',
            quantity: '1',
            notes: ''
        });
        setNewCustomerId('');
        setIsReserveModalOpen(true);
    };

    const handleReserve = async () => {
        const customerId = reserveData.customer_id || newCustomerId;
        
        if (!customerId) {
            toast.error('Selectează sau introdu un ID client');
            return;
        }
        
        const qty = parseInt(reserveData.quantity);
        if (qty > selectedItem.quantity) {
            toast.error('Cantitate insuficientă în magazin');
            return;
        }
        
        try {
            // Use the add-product endpoint for customers
            await api.post(`/customers/${customerId}/add-product`, {
                product_id: selectedItem.product?.id,
                quantity: qty,
                final_price: selectedItem.product?.price || 0
            });
            toast.success(`Rezervat ${qty} buc. pentru clientul ${customerId}`);
            setIsReserveModalOpen(false);
            loadData();
            refreshStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la rezervare');
            console.error(err);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm('Sigur dorești să ștergi acest articol din magazin?')) return;
        
        try {
            await api.delete(`/store/${item.id}`);
            toast.success('Articol șters din magazin');
            loadData();
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergere');
            console.error(err);
        }
    };

    // Move to Depozit
    const openMoveToDepozitModal = (item) => {
        setSelectedItem(item);
        setMoveToDepozitQty('1');
        setMoveToDepozitLocation('P01');
        setIsMoveToDepozitModalOpen(true);
    };

    const handleMoveToDepozit = async () => {
        if (!selectedItem) return;
        
        const qty = parseInt(moveToDepozitQty);
        if (qty > selectedItem.quantity) {
            toast.error('Cantitate insuficientă în magazin');
            return;
        }
        
        try {
            await api.post('/store/move-to-depozit', {
                item_id: selectedItem.id,
                quantity: qty,
                location: moveToDepozitLocation.toUpperCase()
            });
            toast.success(`${qty} buc. mutate în Depozit la ${moveToDepozitLocation}`);
            setIsMoveToDepozitModalOpen(false);
            loadData();
            refreshStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
            console.error(err);
        }
    };

    // Add to Stock Modal
    const openAddStockModal = () => {
        setStep(1);
        setBarcodeInput('');
        setCurrentProduct(null);
        setShowNewProductForm(false);
        setNewProductBarcode('');
        setNewProductName('');
        setNewProductPrice('');
        setNewProductCategory('');
        setStockQuantity('1');
        setIsAddStockModalOpen(true);
        
        setTimeout(() => {
            if (barcodeInputRef.current) {
                barcodeInputRef.current.focus();
            }
        }, 100);
    };

    const handleBarcodeKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            
            if (!barcodeInput.trim()) {
                setNewProductBarcode(generateBarcode());
                setShowNewProductForm(true);
                return;
            }
            
            try {
                const response = await getProductByBarcode(barcodeInput.trim());
                if (response.data) {
                    setCurrentProduct(response.data);
                    setStep(2);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setNewProductBarcode(barcodeInput.trim());
                    setShowNewProductForm(true);
                } else {
                    toast.error('Eroare la căutarea produsului');
                }
            }
        }
    };

    const handleScannedBarcode = async (barcode) => {
        setIsScannerOpen(false);
        setBarcodeInput(barcode);
        
        try {
            const response = await getProductByBarcode(barcode);
            if (response.data) {
                setCurrentProduct(response.data);
                setStep(2);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setNewProductBarcode(barcode);
                setShowNewProductForm(true);
            } else {
                toast.error('Eroare la căutarea produsului');
            }
        }
    };

    // Add existing product to magazin
    const handleAddExistingProduct = async () => {
        if (!currentProduct) return;
        
        try {
            await api.post('/store', {
                product_id: currentProduct.id,
                quantity: parseInt(stockQuantity)
            });
            toast.success(`"${currentProduct.name}" adăugat în Magazin`);
            
            // Reset for next scan
            setBarcodeInput('');
            setCurrentProduct(null);
            setStockQuantity('1');
            setStep(1);
            
            loadData();
            refreshStats();
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            console.error(err);
        }
    };

    // Create new product and add to magazin
    const handleCreateNewProduct = async () => {
        if (!newProductName.trim()) {
            toast.error('Introdu numele produsului');
            return;
        }
        
        try {
            const barcode = newProductBarcode.trim() || generateBarcode();
            const price = parseFloat(newProductPrice) || 0;
            
            // Create product
            const productRes = await api.post('/products', {
                name: newProductName.trim(),
                barcode: barcode,
                price: price,
                category: newProductCategory.trim() || null
            });
            
            // Add to store
            await api.post('/store', {
                product_id: productRes.data.id,
                quantity: parseInt(stockQuantity)
            });
            
            toast.success(`"${newProductName.trim()}" creat și adăugat în Magazin`);
            
            // Reset
            setBarcodeInput('');
            setNewProductBarcode('');
            setNewProductName('');
            setNewProductPrice('');
            setNewProductCategory('');
            setShowNewProductForm(false);
            setStockQuantity('1');
            setStep(1);
            
            loadData();
            refreshStats();
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la creare');
            console.error(err);
        }
    };

    const totalQuantity = storeItems.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div data-testid="store-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Magazin
                    </h1>
                    <p className="text-zinc-500 mt-1">Produse disponibile pentru vânzare</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={exportV1StoreExcel}
                        disabled={isExporting}
                        variant="outline"
                        className="h-11 border-blue-300 text-blue-600 hover:bg-blue-50"
                        data-testid="export-v1-excel-button"
                    >
                        <Download size={18} className={`mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
                        {isExporting ? 'Export...' : 'Export Excel V1'}
                    </Button>
                    <Button
                        onClick={openAddStockModal}
                        data-testid="add-to-stock-button"
                        className="h-11 bg-emerald-500 hover:bg-emerald-600"
                    >
                        <Plus size={18} className="mr-2" />
                        Adaugă în Magazin
                    </Button>
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                        <Store size={20} className="text-emerald-600" />
                        <span className="font-semibold text-emerald-700">{totalQuantity} articole</span>
                    </div>
                </div>
            </div>

            {/* Store Items */}
            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-5 w-3/4 mb-2" />
                            <Skeleton className="h-4 w-1/2 mb-3" />
                            <Skeleton className="h-9 w-full" />
                        </div>
                    ))}
                </div>
            ) : storeItems.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <Store size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">Magazin gol</h3>
                    <p className="text-zinc-500 mt-1">Adaugă produse pentru vânzare</p>
                    <Button onClick={openAddStockModal} className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                        <Plus size={18} className="mr-2" />
                        Adaugă în Magazin
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {storeItems.map((item) => (
                        <div
                            key={item.id}
                            data-testid={`store-item-${item.id}`}
                            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <span className="badge-store text-xs font-medium px-2.5 py-1 rounded-full">
                                    {item.quantity} în magazin
                                </span>
                                <button
                                    onClick={() => handleDelete(item)}
                                    data-testid={`delete-store-${item.id}`}
                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            
                            <h3 className="font-semibold text-zinc-900">
                                {item.product?.name || 'Produs necunoscut'}
                            </h3>
                            
                            {item.product?.barcode && (
                                <p className="text-xs font-mono text-zinc-400 mt-1">
                                    #{item.product.barcode}
                                </p>
                            )}
                            
                            {item.product?.price > 0 && (
                                <p className="text-lg font-bold text-zinc-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    ~{item.product.price.toFixed(2)} RON
                                </p>
                            )}
                            
                            <div className="flex gap-2 mt-4">
                                <Button
                                    onClick={() => openMoveToDepozitModal(item)}
                                    variant="outline"
                                    className="flex-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                                >
                                    <ArrowLeft size={18} className="mr-1" />
                                    Depozit
                                </Button>
                                <Button
                                    onClick={() => openReserveModal(item)}
                                    data-testid={`reserve-button-${item.id}`}
                                    className="flex-1 bg-violet-500 hover:bg-violet-600"
                                >
                                    <BookmarkCheck size={18} className="mr-1" />
                                    Rezervă
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Reserve Modal */}
            <Dialog open={isReserveModalOpen} onOpenChange={setIsReserveModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Rezervă pentru Client TikTok
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <p className="font-medium text-zinc-900">{selectedItem.product?.name}</p>
                                <p className="text-sm text-zinc-500 mt-1">
                                    Disponibil: {selectedItem.quantity} buc.
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="newCustomer">ID Client (ultimele cifre telefon)</Label>
                                <Input
                                    id="newCustomer"
                                    value={newCustomerId}
                                    onChange={(e) => setNewCustomerId(e.target.value)}
                                    data-testid="new-customer-id-input"
                                    placeholder="Ex: 1234"
                                    className="mt-1"
                                    autoFocus
                                />
                            </div>
                            
                            <div>
                                <Label htmlFor="reserveQty">Cantitate</Label>
                                <Input
                                    id="reserveQty"
                                    type="number"
                                    min="1"
                                    max={selectedItem.quantity}
                                    value={reserveData.quantity}
                                    onChange={(e) => setReserveData({ ...reserveData, quantity: e.target.value })}
                                    data-testid="reserve-quantity-input"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsReserveModalOpen(false)}
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleReserve}
                                    data-testid="confirm-reserve-button"
                                    className="flex-1 bg-violet-500 hover:bg-violet-600"
                                    disabled={!newCustomerId}
                                >
                                    <BookmarkCheck size={18} className="mr-2" />
                                    Rezervă
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add to Stock Modal - Similar to Customers/Depozit */}
            <Dialog open={isAddStockModalOpen} onOpenChange={setIsAddStockModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Adaugă în Magazin
                        </DialogTitle>
                    </DialogHeader>
                    
                    {step === 1 && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="flex items-center gap-2">
                                    <ScanBarcode size={18} />
                                    Scanează sau tastează cod
                                </Label>
                                <div className="flex gap-2 mt-1">
                                    <Input
                                        ref={barcodeInputRef}
                                        value={barcodeInput}
                                        onChange={(e) => setBarcodeInput(e.target.value)}
                                        onKeyDown={handleBarcodeKeyDown}
                                        placeholder="Cod de bare..."
                                        className="h-12 font-mono flex-1"
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => setIsScannerOpen(true)}
                                        className="h-12 w-12 bg-[#E11D48] hover:bg-[#BE123C]"
                                        title="Deschide camera"
                                    >
                                        <Camera size={20} />
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mt-1">Enter gol = produs nou</p>
                            </div>
                            
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={stockQuantity}
                                    onChange={(e) => setStockQuantity(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            
                            <Button
                                onClick={() => {
                                    setNewProductBarcode(generateBarcode());
                                    setShowNewProductForm(true);
                                }}
                                variant="outline"
                                className="w-full"
                            >
                                <Plus size={18} className="mr-2" />
                                Produs Nou (fără cod)
                            </Button>
                            
                            <Button onClick={() => setIsAddStockModalOpen(false)} variant="outline" className="w-full">
                                Închide
                            </Button>
                        </div>
                    )}
                    
                    {showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                <p className="text-sm text-emerald-700">Produs nou - se creează și se adaugă în magazin</p>
                            </div>
                            
                            <div>
                                <Label>Cod de Bare</Label>
                                <Input
                                    value={newProductBarcode}
                                    onChange={(e) => setNewProductBarcode(e.target.value)}
                                    placeholder="Auto-generat..."
                                    className="mt-1 font-mono"
                                />
                            </div>
                            
                            <div>
                                <Label>Nume Produs *</Label>
                                <Input
                                    value={newProductName}
                                    onChange={(e) => setNewProductName(e.target.value)}
                                    placeholder="Ex: Set pahare"
                                    className="mt-1"
                                    autoFocus
                                />
                            </div>
                            
                            <div>
                                <Label>Categorie</Label>
                                <Input
                                    value={newProductCategory}
                                    onChange={(e) => setNewProductCategory(e.target.value)}
                                    placeholder="Ex: Bucătărie, Baie, Living"
                                    className="mt-1"
                                    list="store-categories-list"
                                />
                                <datalist id="store-categories-list">
                                    {categories.map((cat, i) => (
                                        <option key={i} value={cat} />
                                    ))}
                                </datalist>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Cantitate</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={stockQuantity}
                                        onChange={(e) => setStockQuantity(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Preț (RON)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={newProductPrice}
                                        onChange={(e) => setNewProductPrice(e.target.value)}
                                        placeholder="0"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setShowNewProductForm(false);
                                        setNewProductBarcode('');
                                        setNewProductName('');
                                        setNewProductPrice('');
                                        setNewProductCategory('');
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button
                                    onClick={handleCreateNewProduct}
                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                                    disabled={!newProductName.trim()}
                                >
                                    Creează + Adaugă
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {step === 2 && currentProduct && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <h3 className="font-semibold text-zinc-900">{currentProduct.name}</h3>
                                <p className="text-sm text-zinc-500">Preț estimat: ~{currentProduct.price?.toFixed(0) || '0'} RON</p>
                                {currentProduct.category && (
                                    <span className="inline-block mt-2 text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded">
                                        {currentProduct.category}
                                    </span>
                                )}
                            </div>
                            
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={stockQuantity}
                                    onChange={(e) => setStockQuantity(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setStep(1);
                                        setBarcodeInput('');
                                        setCurrentProduct(null);
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button onClick={handleAddExistingProduct} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                                    Adaugă în Magazin
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                onScan={handleScannedBarcode}
            />

            {/* Move to Depozit Modal */}
            <Dialog open={isMoveToDepozitModalOpen} onOpenChange={setIsMoveToDepozitModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Mută în Depozit
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <p className="font-medium text-zinc-900">{selectedItem.product?.name}</p>
                                <p className="text-sm text-zinc-500 mt-1">
                                    Disponibil în magazin: {selectedItem.quantity} buc.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="moveQty">Cantitate</Label>
                                    <Input
                                        id="moveQty"
                                        type="number"
                                        min="1"
                                        max={selectedItem.quantity}
                                        value={moveToDepozitQty}
                                        onChange={(e) => setMoveToDepozitQty(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="moveLocation">Locație</Label>
                                    <Select
                                        value={moveToDepozitLocation}
                                        onValueChange={setMoveToDepozitLocation}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map(loc => (
                                                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsMoveToDepozitModalOpen(false)}
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleMoveToDepozit}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                                >
                                    <ArrowLeft size={18} className="mr-2" />
                                    Mută în Depozit
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StorePage;
